import React, { useState, useEffect, useMemo } from 'react';
import { doc, getDoc } from 'firebase/firestore'; 
import { db } from '../firebase/config';
import { createPaymobPaymentSession } from '../services/paymob';
import PolicyModal from './PolicyModal';
import { TERMS_STORAGE_KEY, TERMS_DECLINED_SESSION_KEY } from './TermsBanner';
import { useLanguage } from '../context/LanguageContext';
import { EGYPTIAN_CITIES } from '../constants/cities';

const CheckoutModal = ({ isOpen, onClose, cart, placeOrder, userInfo, onApplyCoupon, couponError, discount, deliveryCosts, isFreeDelivery, currentUser, onLoginClick }) => {
    const { t, getCityLabel } = useLanguage();
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [city, setCity] = useState('');
    const [streetAddress, setStreetAddress] = useState('');
    const [email, setEmail] = useState('');
    const [shippingCost, setShippingCost] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState('creditCard');
    const [couponCode, setCouponCode] = useState('');
    
    // State for the Guest/Login Popup (used for COD guest orders)
    const [showGuestPopup, setShowGuestPopup] = useState(false);

    // State for Terms & Conditions Consent + Policy Modal
    const [agreedToTerms, setAgreedToTerms] = useState(
        () => localStorage.getItem(TERMS_STORAGE_KEY) === 'accepted'
    );
    const [policyModalTab, setPolicyModalTab] = useState(null);

    // State for Paymob Unified Checkout (Intention API)
    const [isInitializingPaymob, setIsInitializingPaymob] = useState(false);
    const [paymobError, setPaymobError] = useState('');
    
    const [paymentSettings, setPaymentSettings] = useState({
        cod: { isActive: true, label: 'Cash on Delivery', fee: 0 },
        creditCard: {
            isActive: true,
            paymobConfigured: true,
            unifiedCheckoutConfigured: true,
            label: 'Credit / Debit Card (Visa & Mastercard)',
            fee: 0,
            testMode: true,
            testIntegrationId: '5990242',
            testPublicKey: 'egy_pk_test_ojLUNmvSxdyf4mSWhRY8BgxQGHPTPPX6',
            testSecretKey: 'egy_sk_test_0d64c3825be44c4b53253b56927d607cb689f8a9065286cf41cb5584f36894f6',
            liveIntegrationId: '6016902'
        }
    });

    useEffect(() => {
        const syncTermsConsent = () => {
            setAgreedToTerms(localStorage.getItem(TERMS_STORAGE_KEY) === 'accepted');
        };
        window.addEventListener('ukiyo-terms-updated', syncTermsConsent);
        return () => window.removeEventListener('ukiyo-terms-updated', syncTermsConsent);
    }, []);

    const handleToggleTerms = (checked) => {
        setAgreedToTerms(checked);
        if (checked) {
            localStorage.setItem(TERMS_STORAGE_KEY, 'accepted');
            sessionStorage.removeItem(TERMS_DECLINED_SESSION_KEY);
            window.dispatchEvent(new Event('ukiyo-terms-updated'));
        }
    };

    useEffect(() => {
        if (isOpen) {
            setPaymobError('');
            setShowGuestPopup(false);
            setAgreedToTerms(localStorage.getItem(TERMS_STORAGE_KEY) === 'accepted');
            const fetchSettings = async () => {
                try {
                    const docRef = doc(db, "storeSettings", "paymentMethods");
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        const isLegacy = !data.creditCard?.paymobConfigured;
                        const isPreUnified = !data.creditCard?.unifiedCheckoutConfigured;
                        const merged = {
                            cod: {
                                isActive: true,
                                label: 'Cash on Delivery',
                                fee: 0,
                                ...(data.cod || {}),
                                ...(isLegacy ? { isActive: true } : {})
                            },
                            creditCard: {
                                isActive: true,
                                paymobConfigured: true,
                                unifiedCheckoutConfigured: true,
                                label: 'Credit / Debit Card (Visa & Mastercard)',
                                fee: 0,
                                liveIntegrationId: '6016902',
                                testIntegrationId: '5990242',
                                ...(data.creditCard || {}),
                                testPublicKey: data.creditCard?.testPublicKey || 'egy_pk_test_ojLUNmvSxdyf4mSWhRY8BgxQGHPTPPX6',
                                testSecretKey: data.creditCard?.testSecretKey || 'egy_sk_test_0d64c3825be44c4b53253b56927d607cb689f8a9065286cf41cb5584f36894f6',
                                testMode: isPreUnified ? true : Boolean(data.creditCard?.testMode),
                                ...(isLegacy ? { isActive: true } : {})
                            }
                        };
                        setPaymentSettings(merged);
                        if (merged.creditCard.isActive) {
                            setPaymentMethod('creditCard');
                        } else if (merged.cod.isActive) {
                            setPaymentMethod('cod');
                        }
                    }
                } catch (err) {
                    console.error("Error loading payment settings:", err);
                }
            };
            fetchSettings();
        }
    }, [isOpen]);

    useEffect(() => {
        if (userInfo) {
            setName(userInfo.name || '');
            setPhone(userInfo.phone || '');
            setCity(userInfo.city || '');
            setStreetAddress(userInfo.address || '');
        }
    }, [userInfo]);

    useEffect(() => {
        if (city) {
            const cityLower = city.toLowerCase();
            if (deliveryCosts && deliveryCosts[cityLower] !== undefined) {
                setShippingCost(deliveryCosts[cityLower]);
            } else if (deliveryCosts) {
                setShippingCost(deliveryCosts.other);
            }
        } else {
            setShippingCost(0);
        }
    }, [city, deliveryCosts]);

    const isCodActive = paymentSettings.cod?.isActive !== false;
    const isCardActive = paymentSettings.creditCard?.isActive !== false;
    const anyPaymentActive = isCodActive || isCardActive;

    const subtotal = useMemo(
        () => cart.reduce((sum, item) => sum + (item.salePrice || item.price) * item.quantity, 0),
        [cart]
    );
    const finalShippingCost = isFreeDelivery ? 0 : shippingCost;
    const paymentFee = paymentMethod === 'creditCard'
        ? (Number(paymentSettings.creditCard?.fee) || 0)
        : (Number(paymentSettings.cod?.fee) || 0);
    const grandTotal = Math.max(0, subtotal - discount + finalShippingCost + paymentFee);

    const customerEmail = currentUser ? currentUser.email : email;
    const isShippingComplete = Boolean(
        name.trim() &&
        phone.trim() &&
        city.trim() &&
        streetAddress.trim() &&
        (currentUser || (email.trim() && email.includes('@')))
    );

    // Keep pending order in localStorage synced whenever shipping details or totals change
    useEffect(() => {
        if (!isOpen || paymentMethod !== 'creditCard') return;
        const pendingOrder = {
            userId: currentUser ? currentUser.uid : null,
            customer: { name, phone, city, address: streetAddress, email: customerEmail },
            items: cart,
            subtotal,
            shipping: finalShippingCost,
            discount,
            coupon: couponCode.toUpperCase(),
            paymentMethod: 'creditCard',
            paymentFee,
            total: grandTotal,
            paymentDetails: null,
            status: 'Placed'
        };
        localStorage.setItem('ukiyo-pending-paymob-order', JSON.stringify(pendingOrder));
    }, [isOpen, paymentMethod, currentUser, name, phone, city, streetAddress, customerEmail, cart, subtotal, finalShippingCost, discount, couponCode, paymentFee, grandTotal]);

    if (!isOpen) return null;

    const handlePlaceOrderClick = async (e) => {
        e.preventDefault();
        if (!anyPaymentActive || !agreedToTerms) return;

        if (paymentMethod === 'creditCard') {
            if (!isShippingComplete) return;
            setIsInitializingPaymob(true);
            setPaymobError('');
            try {
                const resolvedEmail = (customerEmail || 'guest@ukiyo-homes.com').trim();
                const pendingOrder = {
                    userId: currentUser ? currentUser.uid : null,
                    customer: {
                        name: name.trim(),
                        phone: phone.trim(),
                        city: city.trim(),
                        address: streetAddress.trim(),
                        email: resolvedEmail
                    },
                    items: cart,
                    subtotal,
                    shipping: finalShippingCost,
                    discount,
                    coupon: couponCode.toUpperCase(),
                    paymentMethod: 'creditCard',
                    paymentFee,
                    total: grandTotal,
                    paymentDetails: null,
                    status: 'Placed'
                };
                localStorage.setItem('ukiyo-pending-paymob-order', JSON.stringify(pendingOrder));

                const session = await createPaymobPaymentSession({
                    orderDetails: pendingOrder,
                    paymobSettings: paymentSettings.creditCard,
                    redirectionUrl: `${window.location.origin}/`
                });
                const targetUrl = session.checkoutUrl || session.iframeUrl;
                if (targetUrl) {
                    window.location.href = targetUrl;
                } else {
                    throw new Error("Could not retrieve Paymob Unified Checkout URL.");
                }
            } catch (err) {
                console.error("Paymob Unified Checkout error:", err);
                setPaymobError(err.message || "Could not start card checkout. Please try again.");
                setIsInitializingPaymob(false);
            }
            return;
        }

        if (currentUser) {
            finalizeCodOrder();
        } else {
            setShowGuestPopup(true);
        }
    };

    const finalizeCodOrder = () => {
        const orderDetails = {
            userId: currentUser ? currentUser.uid : null,
            customer: { name, phone, city, address: streetAddress, email: customerEmail },
            items: cart,
            subtotal,
            shipping: finalShippingCost,
            discount,
            coupon: couponCode.toUpperCase(),
            paymentMethod: 'cod',
            paymentFee,
            total: grandTotal,
            paymentDetails: null,
            status: 'Placed'
        };

        if (placeOrder) {
            placeOrder(orderDetails);
            setShowGuestPopup(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
            {!showGuestPopup ? (
                /* Main Checkout Modal */
                <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                    <form onSubmit={handlePlaceOrderClick} className="p-6 space-y-6">
                        <div className="flex justify-between items-center border-b pb-4">
                            <h2 className="text-2xl font-bold text-gray-800">{t('checkoutTitle')}</h2>
                            <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl" aria-label="Close">&times;</button>
                        </div>
                        
                        {/* 1. Shipping Info */}
                        <div className="space-y-4">
                            <h3 className="text-lg font-semibold text-gray-700">{t('shippingInfoHeading')}</h3>
                            <div className="grid grid-cols-1 gap-3.5">
                                <input type="text" placeholder={t('fullNamePlaceholder')} value={name} onChange={e => setName(e.target.value)} className="w-full p-3 border rounded-md" required />
                                <input type="tel" placeholder={t('phonePlaceholder')} value={phone} onChange={e => setPhone(e.target.value)} className="w-full p-3 border rounded-md" required />
                                {!currentUser && (
                                    <input
                                        type="email"
                                        placeholder={t('emailInvoicePlaceholder')}
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        className="w-full p-3 border rounded-md"
                                        required
                                    />
                                )}
                                <select aria-label="Select City" value={city} onChange={e => setCity(e.target.value)} className="w-full p-3 border rounded-md bg-white" required>
                                    <option value="">{t('selectCityPlaceholder')}</option>
                                    {EGYPTIAN_CITIES.map(c => <option key={c} value={c}>{getCityLabel(c)}</option>)}
                                </select>
                                <input type="text" placeholder={t('streetAddressPlaceholder')} value={streetAddress} onChange={e => setStreetAddress(e.target.value)} className="w-full p-3 border rounded-md" required />
                            </div>
                        </div>

                        {/* 2. Coupon */}
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">{t('couponPlaceholder')}</label>
                            <div className="flex gap-2">
                                <input type="text" value={couponCode} onChange={e => setCouponCode(e.target.value)} className="flex-1 p-2 border rounded-md uppercase" placeholder={t('couponPlaceholder')} />
                                <button type="button" onClick={() => onApplyCoupon(couponCode)} className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700">{t('apply')}</button>
                            </div>
                            {couponError && <p className="text-red-500 text-sm">{couponError}</p>}
                            {discount > 0 && <p className="text-green-600 text-sm">✓</p>}
                        </div>

                        {/* Store Policies & Terms Agreement */}
                        <div className="p-3.5 bg-gray-50 border border-gray-200 rounded-lg">
                            <label className="flex items-start gap-3 cursor-pointer text-xs sm:text-sm text-gray-700 leading-relaxed">
                                <input
                                    type="checkbox"
                                    checked={agreedToTerms}
                                    onChange={(e) => handleToggleTerms(e.target.checked)}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-[#8B6939] focus:ring-[#b08d57] flex-shrink-0"
                                />
                                <span>
                                    {t('iHaveReadAndAgree')}{' '}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPolicyModalTab('terms');
                                        }}
                                        className="text-[#8B6939] font-semibold underline hover:text-[#b08d57]"
                                    >
                                        {t('termsAndConditions')}
                                    </button>
                                    ,{' '}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPolicyModalTab('return');
                                        }}
                                        className="text-[#8B6939] font-semibold underline hover:text-[#b08d57]"
                                    >
                                        {t('returnAndExchangePolicy')}
                                    </button>{' '}
                                    {t('noCourierInspectionNote')}, {t('andText')}{' '}
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setPolicyModalTab('buying');
                                        }}
                                        className="text-[#8B6939] font-semibold underline hover:text-[#b08d57]"
                                    >
                                        {t('buyingAndShippingPolicy')}
                                    </button>
                                    . *
                                </span>
                            </label>
                        </div>

                        {/* 3. Payment Method */}
                        <div className="space-y-3">
                            <h3 className="text-lg font-semibold text-gray-700">{t('paymentMethodHeading')}</h3>
                            <div className="space-y-3">
                                {/* Option 1: Credit / Debit Card (Paymob) */}
                                {isCardActive && (
                                    <div
                                        className={`border rounded-lg overflow-hidden transition-all ${
                                            paymentMethod === 'creditCard'
                                                ? 'border-[#b08d57] bg-[#b08d57]/5'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <label
                                            onClick={() => setPaymentMethod('creditCard')}
                                            className="flex items-center justify-between p-4 cursor-pointer"
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="radio"
                                                    name="payment"
                                                    value="creditCard"
                                                    checked={paymentMethod === 'creditCard'}
                                                    onChange={() => setPaymentMethod('creditCard')}
                                                    className="text-[#8B6939] focus:ring-[#b08d57]"
                                                />
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-gray-800">{t('creditDebitCard')}</span>
                                                    <span className="text-xs text-gray-500">
                                                        {t('paySecurelyVisaMastercard')}
                                                        {paymentSettings.creditCard?.fee > 0 ? ` (+ EGP ${paymentSettings.creditCard.fee})` : ''}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center justify-end gap-1.5">
                                                {/* Visa Badge */}
                                                <span className="h-6 px-1.5 bg-white border border-gray-200 rounded flex items-center" title="Visa">
                                                    <svg className="h-3 w-auto" viewBox="0 0 780 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <path d="M293.2 348.73L326.56 152.92H379.91L346.54 348.73H293.2Z" fill="#1A1F71" />
                                                        <path d="M540.1 157.7C529.55 153.75 513 149.5 492.32 149.5C439.67 149.5 402.58 176.02 402.27 214.03C401.96 242.14 428.73 257.82 448.92 267.18C469.67 276.82 476.64 282.99 476.55 291.61C476.42 304.8 459.93 310.82 444.57 310.82C423.16 310.82 411.79 307.84 394.22 300.51L387.32 297.37L379.82 341.48C392.32 346.96 415.42 351.72 439.4 351.97C495.59 351.97 532.06 325.66 532.49 285.07C532.7 262.78 518.49 245.81 487.64 231.83C468.97 222.77 457.54 216.73 457.66 207.52C457.66 199.37 467.35 190.66 488.27 190.66C505.75 190.38 518.43 194.2 528.35 198.18L533.17 200.44L540.1 157.7Z" fill="#1A1F71" />
                                                        <path d="M678.5 152.92H637.16C624.36 152.92 614.79 156.41 609.18 169.18L529.8 348.73H585.94C585.94 348.73 595.12 324.6 597.2 319.31C603.34 319.31 657.85 319.39 665.67 319.39C667.27 326.25 672.18 348.73 672.18 348.73H721.81L678.5 152.92ZM612.82 279.31C617.24 267.97 634.12 224.21 634.12 224.21C633.81 224.75 638.51 212.78 641.22 205.35L644.84 222.39C644.84 222.39 655.06 269.62 657.22 279.31H612.82Z" fill="#1A1F71" />
                                                        <path d="M247.92 152.92L195.55 286.68L189.96 259.54C180.24 228.18 149.91 194.15 116.05 177.21L163.93 348.55L220.47 348.48L304.53 152.92H247.92Z" fill="#1A1F71" />
                                                        <path d="M146.92 152.92H60.62L59.93 157.02C127.09 173.32 171.54 212.75 189.96 259.54L171.2 169.47C167.96 156.81 158.59 153.36 146.92 152.92Z" fill="#F9A533" />
                                                    </svg>
                                                </span>
                                                {/* Mastercard Badge */}
                                                <span className="h-6 px-1.5 bg-white border border-gray-200 rounded flex items-center" title="Mastercard">
                                                    <svg className="h-3.5 w-auto" viewBox="0 0 152 100" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                        <circle cx="52" cy="50" r="40" fill="#EB001B" />
                                                        <circle cx="100" cy="50" r="40" fill="#F79E1B" />
                                                        <path d="M76 18.78C86.3 26.1 93 37.35 93 50C93 62.65 86.3 73.9 76 81.22C65.7 73.9 59 62.65 59 50C59 37.35 65.7 26.1 76 18.78Z" fill="#FF5F00" />
                                                    </svg>
                                                </span>
                                                {/* Amex Badge */}
                                                <span className="h-6 px-0.5 bg-white border border-gray-200 rounded flex items-center overflow-hidden" title="American Express">
                                                    <img src="/amex-logo.png" alt="AMEX" className="h-5 w-auto object-contain rounded-[2px]" />
                                                </span>
                                                {/* Meeza Badge */}
                                                <span className="h-6 px-1.5 bg-white border border-gray-200 rounded flex items-center" title="Meeza">
                                                    <img src="/meeza-logo.png" alt="Meeza" className="h-4 w-auto object-contain" />
                                                </span>
                                            </div>
                                        </label>

                                        {paymentMethod === 'creditCard' && paymobError && (
                                            <div className="border-t border-red-200 bg-red-50 p-3 text-center text-red-600 text-xs sm:text-sm">
                                                {paymobError}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Option 2: Cash on Delivery (COD) */}
                                {isCodActive && (
                                    <label
                                        onClick={() => setPaymentMethod('cod')}
                                        className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-all ${
                                            paymentMethod === 'cod'
                                                ? 'border-[#b08d57] bg-[#b08d57]/10'
                                                : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="payment"
                                            value="cod"
                                            checked={paymentMethod === 'cod'}
                                            onChange={() => setPaymentMethod('cod')}
                                            className="text-[#8B6939] focus:ring-[#b08d57]"
                                        />
                                        <div className="flex flex-col">
                                            <span className="font-semibold text-gray-800">{t('cashOnDelivery')}</span>
                                            <span className="text-xs text-gray-500">
                                                {t('payCashUponReceiving')}
                                                {paymentSettings.cod?.fee > 0 ? ` (+ EGP ${paymentSettings.cod.fee})` : ''}
                                            </span>
                                        </div>
                                    </label>
                                )}
                            </div>
                        </div>

                        {/* Order Summary */}
                        <div className="border-t pt-4 space-y-2 text-sm text-gray-600">
                            <div className="flex justify-between"><span>{t('subtotal')}</span><span>EGP {subtotal.toFixed(2)}</span></div>
                            <div className="flex justify-between"><span>{t('shippingLabel')}</span><span>{isFreeDelivery ? <span className="text-green-600">{t('freeLabel')}</span> : `EGP ${shippingCost.toFixed(2)}`}</span></div>
                            {paymentFee > 0 && (
                                <div className="flex justify-between">
                                    <span>{t('paymentFeeLabel')}</span>
                                    <span>EGP {paymentFee.toFixed(2)}</span>
                                </div>
                            )}
                            {discount > 0 && <div className="flex justify-between text-green-600"><span>{t('discountLabel')}</span><span>- EGP {discount.toFixed(2)}</span></div>}
                            <div className="flex justify-between font-bold text-lg text-gray-900 pt-2 border-t"><span>{t('totalLabel')}</span><span>EGP {grandTotal.toFixed(2)}</span></div>
                        </div>

                        {/* Submit Button for Credit Card (Unified Checkout) or Cash on Delivery */}
                        <div className="space-y-2">
                            {!agreedToTerms && (
                                <p className="text-xs text-amber-700 text-center">
                                    {t('checkTermsBeforeOrderWarning')}
                                </p>
                            )}
                            <button 
                                type="submit" 
                                disabled={!city || !anyPaymentActive || !agreedToTerms || isInitializingPaymob || (paymentMethod === 'creditCard' && !isShippingComplete)}
                                className="w-full py-4 bg-[#b08d57] text-white font-bold rounded-lg hover:bg-[#9c7b4d] disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg flex items-center justify-center gap-2"
                            >
                                {isInitializingPaymob ? (
                                    <>
                                        <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                        <span>{t('loadingCardFields')}</span>
                                    </>
                                ) : paymentMethod === 'creditCard' ? (
                                    `${t('creditDebitCard')} — EGP ${grandTotal.toFixed(2)}`
                                ) : (
                                    t('placeOrderCodBtn')
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            ) : (
                /* Guest Popup Modal (for COD) */
                <div className="bg-white rounded-lg shadow-2xl p-6 max-w-sm w-full z-50 text-center" onClick={e => e.stopPropagation()}>
                    <h3 className="text-xl font-bold text-[#8B6939] mb-2">{t('waitSignUpSave')}</h3>
                    <p className="text-gray-600 mb-6">{t('createAccountPromo')}</p>
                    
                    <button 
                        onClick={() => { onClose(); onLoginClick(); }} 
                        className="w-full py-3 bg-[#b08d57] text-white font-bold rounded-md hover:bg-[#9c7b4d] mb-3"
                    >
                        {t('signUpLoginBtn')}
                    </button>
                    
                    <div className="border-t my-4 pt-4">
                        <p className="text-sm text-gray-500 mb-2">{t('orContinueAsGuest')}</p>
                        <input 
                            type="email" 
                            placeholder={t('enterEmailForInvoice')} 
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md mb-3 text-sm"
                        />
                        <button 
                            onClick={() => {
                                if (email && email.includes('@')) {
                                    finalizeCodOrder();
                                } else {
                                    alert("Please enter a valid email address to receive your invoice.");
                                }
                            }} 
                            className="w-full py-2 bg-gray-200 text-gray-800 font-semibold rounded-md hover:bg-gray-300"
                        >
                            {t('continueAsGuestBtn')}
                        </button>
                    </div>
                </div>
            )}

            <PolicyModal
                isOpen={Boolean(policyModalTab)}
                onClose={() => setPolicyModalTab(null)}
                initialTab={policyModalTab || 'terms'}
                onAccept={() => handleToggleTerms(true)}
            />
        </div>
    );
};

export default CheckoutModal;
